import React from 'react';
import { Cpu, WifiOff, CheckCircle2, Terminal } from 'lucide-react';
import { useLive } from '../context/LiveContext';
import MiniSparkline from './MiniSparkline';

const LiveScrapers = () => {
  const { scrapers, logs, featuredScraper } = useLive();
  const activeCount = scrapers.filter((s) => s.status === 'running').length;

  return (
    <section className="rounded-2xl border border-white/5 bg-gradient-to-b from-white/[0.03] to-transparent p-5 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Cpu className="h-4 w-4 text-cyan-400" />
          <p className="text-[10px] uppercase tracking-[0.25em] text-white/70 font-mono">Scrapers actifs</p>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" />
          </span>
          <span className="text-[10px] font-mono text-emerald-300">{activeCount} EN COURS</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        {scrapers.map((s, idx) => (
          <ScraperCard key={s.id} s={s} delay={idx * 0.05} isFeatured={s.site === featuredScraper?.site} />
        ))}
      </div>

      <div className="flex-1 rounded-lg border border-emerald-400/15 bg-black/60 overflow-hidden flex flex-col min-h-[140px]">
        <div className="px-3 py-1.5 flex items-center gap-2 border-b border-white/5 bg-white/[0.02]">
          <Terminal className="h-3 w-3 text-emerald-400" />
          <span className="text-[9px] font-mono text-white/50 uppercase tracking-widest">hermes.stream</span>
          <span className="ml-auto flex gap-1">
            <span className="h-1.5 w-1.5 rounded-full bg-red-400/60" />
            <span className="h-1.5 w-1.5 rounded-full bg-amber-400/60" />
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400/60" />
          </span>
        </div>
        <div className="flex-1 overflow-hidden font-mono text-[10px] leading-relaxed px-3 py-2 relative">
          <div className="space-y-0.5">
            {logs.slice(0, 8).map((line, i) => (
              <div
                key={i + line}
                className="text-emerald-300/80 whitespace-nowrap overflow-hidden text-ellipsis"
                style={{ opacity: 1 - i * 0.11, animation: i === 0 ? 'log-in 0.4s ease' : undefined }}
              >
                <span className="text-white/25 mr-1">›</span>{line}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const ScraperCard = ({ s, delay, isFeatured }) => {
  const isRunning = s.status === 'running';
  const isQueued = s.status === 'queued';
  const isDone = s.status === 'done';

  return (
    <div
      className="relative rounded-lg border bg-black/40 p-3 overflow-hidden group"
      style={{
        borderColor: isFeatured ? '#ec489988' : isRunning ? `${s.color}55` : 'rgba(255,255,255,0.06)',
        boxShadow: isFeatured ? '0 0 25px rgba(236,72,153,0.3)' : isRunning ? `0 0 20px ${s.color}22` : 'none',
        animation: `card-in 0.5s ease ${delay}s both`,
        transition: 'border-color 0.4s, box-shadow 0.4s',
      }}
    >
      {isFeatured && (
        <div className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-pink-500/20 border border-pink-500/40">
          <span className="text-[8px] font-mono text-pink-200 uppercase">Radar</span>
        </div>
      )}

      {isRunning && (
        <div className="absolute inset-0 pointer-events-none opacity-40">
          <div className="absolute inset-x-0 top-0 h-px" style={{ background: `linear-gradient(90deg, transparent, ${s.color}, transparent)`, animation: 'stream-x 2s linear infinite' }} />
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5">
          {isRunning && <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: s.color, boxShadow: `0 0 6px ${s.color}` }} />}
          {isQueued && <WifiOff className="h-3 w-3 text-white/40" />}
          {isDone && <CheckCircle2 className="h-3 w-3 text-emerald-400" />}
          <span className="font-mono text-[11px] text-white truncate max-w-[110px]">{s.site}</span>
        </div>
        <span className="text-[9px] font-mono text-white/40">{s.country}</span>
      </div>

      <div className="h-1.5 rounded-full bg-white/[0.05] overflow-hidden mb-2 relative">
        <div
          className="h-full rounded-full transition-all duration-700 ease-out"
          style={{
            width: `${s.progress}%`,
            background: isRunning ? `linear-gradient(90deg, ${s.color}88, ${s.color})` : 'rgba(255,255,255,0.15)',
            boxShadow: isRunning ? `0 0 8px ${s.color}` : 'none',
          }}
        />
        {isRunning && (
          <div className="absolute inset-y-0 w-8 pointer-events-none" style={{ background: `linear-gradient(90deg, transparent, ${s.color}88, transparent)`, animation: 'sweep 1.5s ease-in-out infinite' }} />
        )}
      </div>

      {isRunning && <MiniSparkline color={s.color} />}

      <div className="grid grid-cols-3 gap-1 mt-2">
        <MiniMetric label="P" value={s.pages} />
        <MiniMetric label="▣" value={s.products} />
        <MiniMetric label="ms" value={Math.round(s.latency)} />
      </div>

      {isQueued && (
        <div className="absolute inset-0 bg-black/50 backdrop-blur-[1px] grid place-items-center">
          <span className="text-[10px] font-mono text-white/60 uppercase tracking-widest">En file</span>
        </div>
      )}
    </div>
  );
};

const MiniMetric = ({ label, value }) => (
  <div className="text-center">
    <div className="font-mono text-xs text-white/90 tabular-nums leading-none">{value}</div>
    <div className="font-mono text-[8px] text-white/35 uppercase mt-0.5">{label}</div>
  </div>
);

export default LiveScrapers;
